import { useState } from "react";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import {
  getValidationSchema,
  initialValues as initialValuesBase,
} from "./config";
import { Box } from "@mui/material";
import { StyledFormicError } from "./styles";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import { green } from "@mui/material/colors";
import { Species } from "types/species";
import {
  useAddSpeciesMutation,
  useUpdateSpeciesMutation,
} from "graphql/__generated__/hooks";
import { Modal } from "components/Modal";
import { useNotifications } from "hooks/useNotifications";
import { Messages, NotificationSeverity } from "helpers/constants";

export type Props = {
  createOrEditSpeciesModal: boolean;
  handleCreateOrEditSpeciesModalDismiss: () => void;
  handleCreateOrEditSpeciesChange: (species_id?: Species) => string | void;
  species?: Species | null;
};

const CreateOrEditSpeciesModal = ({
  createOrEditSpeciesModal,
  handleCreateOrEditSpeciesModalDismiss,
  handleCreateOrEditSpeciesChange,
  species = null,
}: Props) => {
  const isEditMode = species !== null;
  const [loading, setLoading] = useState(false);
  const [addSpecies] = useAddSpeciesMutation();
  const [updateSpecies] = useUpdateSpeciesMutation();
  const { notify } = useNotifications();

  const handleCreateSpecies = async (reqValues: Species) => {
    setLoading(true);

    await addSpecies({
      variables: reqValues,
    });
    handleCreateOrEditSpeciesChange();
    setLoading(false);
    notify({
      message: Messages.successSpeciesCreated,
      severity: NotificationSeverity.success,
    });
  };

  const handleEditSpecies = async (reqValues: Species) => {
    setLoading(true);

    await updateSpecies({
      variables: {
        id: species?.species_id ?? "",
        species: reqValues,
      },
    });
    handleCreateOrEditSpeciesChange();
    setLoading(false);
    notify({
      message: Messages.successSpeciesUpdated,
      severity: NotificationSeverity.success,
    });
  };

  const initialValues = isEditMode
    ? {
        speciesName: species?.name || "",
      }
    : initialValuesBase;

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: getValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const reqValues = {
        name: values.speciesName,
      };
      if (isEditMode) {
        handleEditSpecies(reqValues as Species).then();
      } else {
        handleCreateSpecies(reqValues as Species).then();
      }
    },
  });

  return (
    <Modal
      open={createOrEditSpeciesModal}
      onClose={handleCreateOrEditSpeciesModalDismiss}
      title={isEditMode ? species?.name : "Create a new Species"}
      size="small"
    >
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ mt: 1 }}>
            <Box>
              <TextField
                variant="standard"
                onChange={formik.handleChange}
                margin="dense"
                fullWidth
                name="speciesName"
                label="Species Name"
                type="text"
                id="speciesName"
                value={formik?.values?.speciesName}
              />
              <ErrorMessage name="speciesName" component={StyledFormicError} />
            </Box>

            <Grid sx={{ mt: 5, display: "flex", justifyContent: "flex-end" }}>
              <Box mr={2}>
                <Button
                  variant="outlined"
                  onClick={handleCreateOrEditSpeciesModalDismiss}
                >
                  Cancel
                </Button>
              </Box>
              <Box>
                <Button
                  disabled={loading}
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Submit
                </Button>
              </Box>
              {loading && (
                <CircularProgress
                  size={30}
                  sx={{
                    color: green[500],
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Grid>
          </Box>
        </form>
      </FormikProvider>
    </Modal>
  );
};

export default CreateOrEditSpeciesModal;
